import { useMemo } from 'react';
import { scaleSequential } from 'd3-scale';
import { interpolateYlGnBu } from 'd3-scale-chromatic';

/**
 * Custom hook to generate color scales based on a sequential color scheme.
 *
 * @param {object} config - Configuration object for colors.
 * @param {function} [config.interpolateFunction=interpolateBlues] - The D3 interpolation function.
 * @param {number} [config.numberOfShades=5] - Number of color shades to generate.
 * @param {number} [config.minDomain=0] - Minimum value for the domain.
 * @param {number} [config.maxDomain=1] - Maximum value for the domain.
 * @returns {string[]} Array of color values.
 */
const useColors = ({
  interpolateFunction = interpolateYlGnBu,
  numberOfShades = 5,
  minDomain = 0,
  maxDomain = 1,
}) => {
  const interpolateColors = interpolateFunction || interpolateYlGnBu;
  const validNumberOfShades = Math.max(1, Math.round(numberOfShades));
  const colorScale = useMemo(() => {
    return scaleSequential(interpolateColors).domain([minDomain, maxDomain]);
  }, [interpolateFunction, minDomain, maxDomain]);

  const colors = useMemo(() => {
    return Array.from({ length: validNumberOfShades }, (_, i) =>
      colorScale(i / (validNumberOfShades - 1) * (maxDomain - minDomain) + minDomain)
    ).reverse()
  }, [colorScale, validNumberOfShades, minDomain, maxDomain]);

  return colors;
};

export default useColors;
